import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import moment from "moment";
import dashboardServices from "app/services/dashboard-services";

// El panel de la tarjeta "Fuera de horario" de la cabecera de Viajes.
//
// Dos preguntas y nada mas: cuanto sobretiempo acumula cada chofer, y cuales son las
// entregas que se pasaron. Antes esto abria con un histograma de cierres por hora, pero
// dibujaba sobre todo lo que cerro DENTRO de la jornada -que es lo normal y no es
// noticia- y dejaba lo que importa como dos barras al borde del grafico.
//
// El naranja es el mismo que el resto del dashboard ya usa para marcar lo que necesita
// atencion.
const FUERA = "#EA580C";

const WorkdayOverflow = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [desde, setDesde] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
    const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));

    const cargar = useCallback(() => {
        setLoading(true);
        setError(null);

        dashboardServices
            .getWorkdayOverflow({desde, hasta})
            .then((res) => setData(res))
            .catch(() => setError("No se pudo cargar el desborde de jornada."))
            .finally(() => setLoading(false));
    }, [desde, hasta]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    const totales = data?.totals;
    const cerradas = totales?.deliveries_closed ?? 0;
    const sinJornada = data && (
        data.configured === false
        || ((data.configured === undefined || data.configured === null) && !data.window?.start)
    );

    return (
        <Card sx={{mb: 3}}>
            <CardContent>
                <Stack
                    direction={{xs: "column", md: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "flex-start", md: "center"}}
                    spacing={1.5}
                    sx={{mb: 2}}
                >
                    <Stack spacing={0.5}>
                        <Typography variant="h4" sx={{mb: 0}}>Fuera de horario</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Las entregas que se cerraron fuera de la jornada configurada.
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1}>
                        <TextField
                            id="workday-overflow-desde"
                            label="Desde"
                            type="date"
                            size="small"
                            value={desde}
                            onChange={(e) => setDesde(e.target.value)}
                            InputLabelProps={{shrink: true}}
                        />
                        <TextField
                            id="workday-overflow-hasta"
                            label="Hasta"
                            type="date"
                            size="small"
                            value={hasta}
                            onChange={(e) => setHasta(e.target.value)}
                            InputLabelProps={{shrink: true}}
                        />
                    </Stack>
                </Stack>

                {loading && (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                        <CircularProgress/>
                    </Box>
                )}

                {!loading && error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && sinJornada && (
                    <Alert severity="info" sx={{mb: 2}}>
                        Esta empresa no tiene jornada configurada, así que no hay contra qué comparar.
                        Se define en la configuración de ruteo, y puede afinarse por vehículo o por chofer.
                    </Alert>
                )}

                {!loading && !error && !sinJornada && (
                    <>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{mb: 2}}>
                            {data?.window?.start && data?.window?.end ? (
                                <Chip
                                    size="small"
                                    label={`Jornada ${data.window.start} – ${data.window.end}`}
                                    sx={{fontWeight: 600}}
                                />
                            ) : (
                                <Chip
                                    size="small"
                                    label="Turno configurado por conductor o vehículo"
                                    sx={{fontWeight: 600}}
                                />
                            )}
                            <Chip
                                size="small"
                                label={`${totales?.out_of_hours_deliveries ?? 0} entregas fuera`}
                                sx={{fontWeight: 600, color: FUERA, bgcolor: `${FUERA}14`, border: `1px solid ${FUERA}66`}}
                            />
                            <Chip
                                size="small"
                                label={`${totales?.out_of_hours_trips ?? 0} viajes afectados`}
                                sx={{fontWeight: 600}}
                            />
                            <Chip
                                size="small"
                                label={`${cerradas} cerradas en el período`}
                                sx={{fontWeight: 600}}
                            />
                        </Stack>

                        {cerradas === 0 && (
                            <Alert severity="info">No hay entregas cerradas en este período.</Alert>
                        )}

                        {cerradas > 0 && (data?.by_driver || []).length === 0 && (
                            <Alert severity="success">
                                Ninguna entrega se cerró fuera de la jornada en este período.
                            </Alert>
                        )}

                        {(data?.by_driver || []).length > 0 && (
                            <TableContainer>
                                <Typography variant="h6" sx={{mb: 1}}>Sobretiempo por chofer</Typography>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Chofer</TableCell>
                                            <TableCell align="right">Entregas fuera</TableCell>
                                            <TableCell align="right">Viajes</TableCell>
                                            <TableCell align="right">Sobretiempo</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.by_driver.map((fila) => (
                                            <TableRow key={fila.driver_id || fila.driver}>
                                                <TableCell>{fila.driver}</TableCell>
                                                <TableCell align="right">{fila.deliveries}</TableCell>
                                                <TableCell align="right">{fila.trips}</TableCell>
                                                <TableCell align="right">
                                                    {Math.floor(fila.overtime_min / 60)}h {fila.overtime_min % 60}m
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        {(data?.offenders || []).length > 0 && (
                            <Box sx={{mt: 3}}>
                                {/* La tabla que contesta "cuales se pasaron". El turno va en cada
                                    fila y no solo en la cabecera del panel: cada chofer puede tener
                                    el suyo, y sin eso una fila que se paso 2h 50m no se explica sola. */}
                                <Typography variant="h6" sx={{mb: 1}}>Las que se pasaron</Typography>
                                <TableContainer sx={{maxHeight: 420}}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell>Pedido</TableCell>
                                                <TableCell>Conductor</TableCell>
                                                <TableCell align="center">Turno</TableCell>
                                                <TableCell align="right">Sobretiempo</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.offenders.map((fila) => (
                                                <TableRow key={fila.delivery_id}>
                                                    <TableCell>
                                                        {fila.ship_date ? moment(fila.ship_date).format("DD/MM") : "—"}
                                                    </TableCell>
                                                    <TableCell>{fila.order_code || "—"}</TableCell>
                                                    <TableCell>{fila.driver}</TableCell>
                                                    <TableCell align="center" sx={{whiteSpace: "nowrap"}}>
                                                        {fila.workday_start} - {fila.workday_end}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {fila.overtime_min > 0
                                                            ? `${Math.floor(fila.overtime_min / 60)}h ${fila.overtime_min % 60}m`
                                                            : "antes de abrir"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default WorkdayOverflow;
